import React, {useCallback, useImperativeHandle, useMemo, useState} from 'react';
import _ from 'lodash';
import {IconButton, MenuItem, Select, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SCTextField from './SCTextField.js';
import {closeModal, openModal} from './Modals.js';

const DAYS_OF_WEEK = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

const MONTH_NAMES = [
    'ינואר',
    'פברואר',
    'מרץ',
    'אפריל',
    'מאי',
    'יוני',
    'יולי',
    'אוגוסט',
    'ספטמבר',
    'אוקטובר',
    'נובמבר',
    'דצמבר',
];

const YEAR_OPTIONS = (()=>{
    let currentYear = new Date().getFullYear();
    let from = currentYear-50;
    let to = currentYear+50;
    let res = [];
    while ((from++)<to)
        res.push({label: from, value: from});
    return res;
})();

const MONTH_OPTIONS = MONTH_NAMES.map((m, i)=>({value: i, label: m}));

const fmt = ms=>{
    const d = ms ? new Date(ms) : new Date();
    let month = ''+(d.getMonth()+1);
    let day = ''+d.getDate();
    let year = d.getFullYear();
    if (month.length<2) 
        month = '0'+month;
    if (day.length<2) 
        day = '0'+day;
    return `${day}/${month}/${year}`;
};

const YearDropdown = props=>{
    let {value, onChange: _onChange} = props;
    let onChange = useCallback(ev=>_onChange?.(ev.target.value, ev), [_onChange]);
    return <Select value={value} onChange={onChange}>
      {YEAR_OPTIONS.map(i=><MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>)}
    </Select>;
};

const MonthDropdown = props=>{
    let {value, onChange: _onChange} = props;
    let onChange = useCallback(ev=>_onChange?.(ev.target.value, ev), [_onChange]);
    return <Select value={value} onChange={onChange}>
      {MONTH_OPTIONS.map(i=><MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>)}
    </Select>;
};

const StyledBaseCell = styled(TableCell)`
    padding: 8px;
`;

const StyledDateCell = styled(StyledBaseCell, {shouldForwardProp: p=>!p.startsWith('$')})`
    border-radius: 8px;
    ${p=>p.$selected ? `
        font-weight: bold;
        background-color: ${p.theme.palette.primary.main};
        color: ${p.theme.palette.primary.contrastText};
    ` : p.$today ? `
        background-color: ${p.theme.palette.info.light};
    ` : ''}
    ${p=>p.$hasDay ? `
        cursor: pointer;
        &:hover {
            background-color: ${p.theme.palette.primary.dark};
            color: ${p.theme.palette.primary.contrastText};
        }
    ` : ''}
`;

const DateCell = props=>{
    let {value, year, month, day, onChange} = props;
    let selected = useMemo(
        ()=>value.getFullYear()==year&&value.getMonth()==month&&value.getDate()==day,
        [value, year, month, day]);
    let today = useMemo(()=>{
        let today = new Date();
        return today.getFullYear()==year&&today.getMonth()==month&&today.getDate()==day;
    }, [year, month, day])
    let onClick = useCallback(()=>day&&onChange(new Date(year, month, day)), [year, month, day]);
    return <StyledDateCell $selected={selected} $today={today} $hasDay={!!day} align="center" onClick={onClick}>
      {day===null ? '' : day}
    </StyledDateCell>;
};

const HeaderCell = props=>{
    let {children, ...rest} = props;
    return <StyledBaseCell align="center" {...rest}>
      <Typography align="center" fontWeight="bold">
        {children}
      </Typography>
    </StyledBaseCell>;
}

const DayPicker = props=>{
    let {value, year, month, onChange} = props;
    let rows = useMemo(()=>{
        let daysInMonth = new Date(year, month, 0).getDate();
        let firstDayOfWeek = new Date(year, month, 1).getDay();
        let res = [[]];
        for (let i = 0; i<firstDayOfWeek; i++)
            res[0].push(null);
        for (let j = 0; j<daysInMonth; j++) {
            if (res[res.length-1].length==7)
                res.push([]);
            res[res.length-1].push(j+1);
        }
        return res;
    }, [year, month]);
    return <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {DAYS_OF_WEEK.map(d=><HeaderCell key={d}>{d}</HeaderCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i)=><TableRow key={i}>
            {row.map((cell, j)=><DateCell key={j}
              value={value} year={year} month={month} day={cell} onChange={onChange}/>)}
          </TableRow>)}
        </TableBody>
      </Table>
    </TableContainer>;
};

const DatePickerModal = props=>{
    let {value=new Date(), onChange: _onChange} = props;
    let [year, setYear] = useState(value.getFullYear());
    let [month, setMonth] = useState(value.getMonth());
    let prevMonth = useCallback(()=>{
        if (month==0) {
            setMonth(11);
            setYear(year-1);
            return;
        }
        setMonth(month-1);
    }, [year, month]);
    let nextMonth = useCallback(()=>{
        if (month==11) {
            setMonth(0);
            setYear(year+1);
            return;
        }
        setMonth(month+1);
    }, [year, month]);
    let prevDisabled = useMemo(()=>year==YEAR_OPTIONS[0].value&&month==0, [year, month]);
    let nextDisabled = useMemo(()=>year==YEAR_OPTIONS[YEAR_OPTIONS.length-1].value&&month==11, [year, month]);
    let onChange = useCallback(v=>{
        _onChange(v);
        closeModal();
    }, [_onChange]);
    return <Stack direction="column">
      <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
        <IconButton onClick={prevMonth} disabled={prevDisabled}>
          <ChevronRightIcon/>
        </IconButton>
        <Stack direction="row">
          <MonthDropdown value={month} onChange={setMonth}/>
          <YearDropdown value={year} onChange={setYear}/>
        </Stack>
        <IconButton onClick={nextMonth} disabled={nextDisabled}>
          <ChevronLeftIcon/>
        </IconButton>
      </Stack>
      <DayPicker value={value} year={year} month={month} onChange={onChange}/>
    </Stack>;
};

const SCDatePicker = React.forwardRef((props, ref)=>{
    let {
        value,
        onChange,
        label,
        onClick: _onClick,
        ...rest
    } = props;
    let theme = useTheme();
    let [fieldEl, setFieldEl] = useState();
    let open = useCallback(()=>openModal({
        header: label,
        content: <DatePickerModal value={value} onChange={onChange}/>,
    }), [label, value, onChange]);
    let onClick = useCallback(()=>{
        open();
        _onClick?.();
    }, [open, _onClick]);
    let formattedValue = useMemo(()=>fmt(value), [value]);
    useImperativeHandle(ref, ()=>({field: fieldEl, open}), [fieldEl, open]);
    return <SCTextField
      ref={setFieldEl}
      onClick={onClick}
      label={label}
      value={formattedValue}
      onChange={_.noop}
      InputProps={{endAdornment: <CalendarMonthIcon fontSize="small"
        sx={{color: theme.palette.grey['600'], marginLeft: '8px'}}/>}}
      {...rest}/>;
});

export default SCDatePicker;
